// ── Tax monitoring coverage: what state is an entity in? ────────────────────
//
// Lifted out of TaxMonitoringPage so the dashboard card can ask the same
// question and get the same answer. It was about to be reimplemented there as
// "no auth_to means unmonitored", which is wrong in the direction that matters:
// it counts a borrower we simply have not synced yet as a coverage gap, and a
// gap number that is reliably too high is one people learn to ignore.
//
// One definition, two readers. The dashboard tile and the Coverage card cannot
// disagree about how many borrowers nobody is watching.

// Why a portfolio manager decided an entity does not need watching.
// `no_payroll` was withdrawn — see api/saveTaxCoverageExemption — but is kept in
// the map so a row recorded under it before the change still reads as words.
export const EXEMPT_REASON = {
  no_payroll:        "no payroll",
  dormant:           "dormant",
  covered_elsewhere: "covered elsewhere",
  individual:        "individual",
  other:             "other",
};

// An 8821 is the gate. No current authorization means no monitoring at all, and
// it fails silently — a borrower with a lapsed 8821 returns no findings, which
// reads exactly like a borrower with nothing to find.
export const authState = e => {
  // ── A DECISION OUTRANKS A GAP ──
  // A portfolio manager has said this entity does not need watching. That is an
  // answer, so it is grey and it is not counted as unmonitored — the whole point
  // is that "Not monitored" can reach zero. It deliberately sits ABOVE the
  // 8821 states: an exempt entity with a lapsed authorisation is not a problem,
  // it is an entity nobody is chasing an authorisation for.
  //
  // A rejection still shows through, because the IRS refusing an 8821 we DID
  // submit is a fact worth seeing whatever we have since decided.
  if (e.exempt_reason && !e.auth_rejected_on) {
    return { key: "exempt", label: "Not tracked · " + (EXEMPT_REASON[e.exempt_reason] || e.exempt_reason),
             bg: "#F1EFE8", fg: "#6b6454" };
  }
  if (e.auth_rejected_on) return { key: "rejected", label: "8821 rejected", bg: "#FBE4DD", fg: "#993C1D" };
  if (!e.taxguardclientid) return { key: "none", label: "Not enrolled", bg: "#F1EFE8", fg: "#888780" };
  // ── "WE HAVE NOT LOOKED" IS NOT "THERE IS NONE" ──
  // This used to fall straight to "No 8821" on a null auth_to, which meant a
  // borrower whose authorization is current in Tax Guard's portal — uploaded
  // 03/2024, good to 12/2027 — showed a red badge claiming there was none.
  // The badge has to be trustworthy in the direction that matters: a lapsed
  // 8821 fails silently, so the red state is the one people must be able to act
  // on without checking. An alarm that cries wolf for "not yet synced" is worse
  // than no alarm, because it teaches people to scroll past the real one.
  if (!e.auth_checked_at) return { key: "unchecked", label: "8821 not checked", bg: "#F1EFE8", fg: "#888780" };
  if (!e.auth_to) return { key: "noauth", label: "No 8821", bg: "#FBE4DD", fg: "#993C1D" };
  const days = Math.round((new Date(e.auth_to) - new Date()) / 86400000);
  if (days < 0) return { key: "expired", label: "8821 expired", bg: "#FBE4DD", fg: "#993C1D" };
  if (days <= 60) return { key: "expiring", label: "8821 expires " + days + "d", bg: "#FDF0D5", fg: "#7A5200" };
  return { key: "ok", label: "Monitored", bg: "#E1F5EE", fg: "#0F6E56" };
};

// The states that mean a finding could NOT reach us. "unchecked" and "expiring"
// are deliberately out: one is a sync that has not run, the other is still
// current — counting either as a gap would be counting work that does not exist.
export const UNMONITORED = ["none", "noauth", "expired", "rejected"];

export const isUnmonitored = e => UNMONITORED.includes(authState(e).key);
